import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DatabaseBackup, Download, FileArchive, RefreshCw, ShieldAlert, Store, UserRound, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import Select from '../../components/Select';
import API from '../../utils/api';
import './Backup.css';

const SCOPES = [
    { id: 'both', label: 'Members and service providers', Icon: Users },
    { id: 'members', label: 'Members only', Icon: UserRound },
    { id: 'providers', label: 'Service providers only', Icon: Store },
];

const monthName = month => {
    const [year, m] = month.split('-').map(Number);
    return new Date(year, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

const Backup = () => {
    const [summary, setSummary] = useState(null);
    const [period, setPeriod] = useState('all');
    const [scope, setScope] = useState('both');
    const [busy, setBusy] = useState(false);
    const [lastFile, setLastFile] = useState('');

    const load = useCallback(async () => {
        try {
            const { data } = await API.get('/admin/backup/summary');
            setSummary(data);
        } catch {
            toast.error('Could not load the backup summary');
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const current = useMemo(() => {
        if (!summary) return { members: 0, providers: 0 };
        if (period === 'all') return summary.totals;
        return summary.months.find(m => m.month === period) || { members: 0, providers: 0 };
    }, [summary, period]);

    const thisMonth = useMemo(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    }, []);

    const download = async (chosenPeriod = period) => {
        setBusy(true);
        try {
            const res = await API.get('/admin/backup/download', { params: { period: chosenPeriod, scope }, responseType: 'blob', timeout: 300000 });
            const disposition = res.headers?.['content-disposition'] || '';
            const name = (/filename="?([^";]+)"?/i.exec(disposition) || [])[1] || `gettimelam-backup-${chosenPeriod}.zip`;
            const url = URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = name;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            setLastFile(name);
            toast.success('Backup downloaded');
        } catch (error) {
            // an error body arrives as a blob here, so read its text
            let message = 'Could not create the backup';
            try { message = JSON.parse(await error.response.data.text()).message || message; } catch { /* keep the default */ }
            toast.error(message);
        } finally {
            setBusy(false);
        }
    };

    const options = [
        { value: 'all', label: 'All data (full backup)' },
        ...(summary?.months || []).map(m => ({ value: m.month, label: `${monthName(m.month)} · ${m.members} members, ${m.providers} providers` })),
    ];

    return (
        <div className="backup-layout">
            <Sidebar />
            <main className="admin-content backup-content">
                <Navbar />
                <div className="backup-page">
                    <header className="backup-heading">
                        <div>
                            <div className="backup-eyebrow"><DatabaseBackup size={14} /> Settings</div>
                            <h1>Backup</h1>
                            <p>Download member and service provider data from the database as a ZIP of Excel sheets: every detail on the account, the profile, service listings and subscriptions.</p>
                        </div>
                        <button type="button" className="backup-refresh" onClick={load}><RefreshCw size={16} /> Refresh</button>
                    </header>

                    <section className="backup-card">
                        <div className="backup-step">
                            <h2><span>1</span> What to include</h2>
                            <div className="backup-scopes">
                                {SCOPES.map(({ id, label, Icon }) => (
                                    <button key={id} type="button" className={scope === id ? 'is-active' : ''} onClick={() => setScope(id)}>
                                        <Icon size={18} />{label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="backup-step">
                            <h2><span>2</span> Which period</h2>
                            <Select value={period} onChange={setPeriod} options={options} allowEmpty={false} ariaLabel="Backup period" />
                            <p className="backup-hint">
                                {period === 'all'
                                    ? 'Every member and service provider account, from the first day.'
                                    : `Only accounts that registered in ${monthName(period)}.`}
                            </p>
                        </div>

                        <div className="backup-summary">
                            <div><strong>{scope === 'providers' ? '—' : current.members}</strong><span>Members</span></div>
                            <div><strong>{scope === 'members' ? '—' : current.providers}</strong><span>Service providers</span></div>
                        </div>

                        <div className="backup-actions">
                            <button type="button" className="backup-download" onClick={() => download()} disabled={busy}>
                                <Download size={17} />{busy ? 'Preparing the ZIP…' : 'Download backup (ZIP)'}
                            </button>
                            <button type="button" className="backup-month" onClick={() => { setPeriod(thisMonth); download(thisMonth); }} disabled={busy}>
                                <FileArchive size={16} /> This month ({monthName(thisMonth)})
                            </button>
                        </div>
                        {lastFile && <p className="backup-last">Last download: <b>{lastFile}</b></p>}
                    </section>

                    <section className="backup-note">
                        <ShieldAlert size={18} />
                        <div>
                            <strong>Keep backups private</strong>
                            <p>The files contain personal details. Passwords, OTP and login tokens, Aadhaar numbers and ID card images are never included. Back up once a month and store the ZIP somewhere safe.</p>
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
};

export default Backup;
